import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authService, Teacher } from '../services';
import { apiClient } from '../services/api';

export interface AuthContextType {
  user: Teacher | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateProfile: (updates: Partial<Pick<Teacher, 'email'>>) => Promise<void>;
  error: string | null;
  clearError: () => void;
  onAuthError?: (error: { message: string; type: string }) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export interface AuthProviderProps {
  children: ReactNode;
  onAuthError?: (error: { message: string; type: string }) => void;
}

export function AuthProvider({ children, onAuthError }: AuthProviderProps) {
  const [user, setUser] = useState<Teacher | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => setError(null);

  // Initialize auth state on app start
  useEffect(() => {
    initializeAuth();
    
    // Set up global auth error handler
    if (onAuthError) {
      apiClient.setAuthErrorHandler((apiError) => {
        onAuthError({ message: apiError.message, type: apiError.type });
      });
    }
    
    return () => {
      apiClient.removeAuthErrorHandler();
    };
  }, [onAuthError]);

  const initializeAuth = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (authService.isAuthenticated()) {
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
      }
    } catch (err: any) {
      console.warn('Auth initialization failed:', err);
      // Don't set error for initialization failure - user might not be logged in
      await authService.logout(); // Clear any invalid tokens
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await authService.login({ email, password });
      setUser(response.teacher);
    } catch (err: any) {
      const errorMessage = err?.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      await authService.logout();
      setUser(null);
    } catch (err: any) {
      console.warn('Logout error:', err);
      // Clear user state even if logout API call fails
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      setError(null);
      
      if (!authService.isAuthenticated()) {
        setUser(null);
        return;
      }

      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (err: any) {
      console.warn('User refresh failed:', err);
      setError('Session expired. Please log in again.');
      await logout();
    }
  };

  const updateProfile = async (updates: Partial<Pick<Teacher, 'email'>>) => {
    try {
      setError(null);
      
      const updatedUser = await authService.updateProfile(updates);
      setUser(updatedUser);
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to update profile';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const contextValue: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
    updateProfile,
    error,
    clearError,
    onAuthError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Additional utility hooks
export function useAuthUser(): Teacher | null {
  const { user } = useAuth();
  return user;
}

export function useAuthStatus(): {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
} {
  const { isAuthenticated, isLoading, error } = useAuth();
  return { isAuthenticated, isLoading, error };
}

export function useRequireAuth(): Teacher {
  const { user, isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    throw new Error('Authentication is still loading');
  }
  
  if (!isAuthenticated || !user) {
    throw new Error('Authentication required');
  }
  
  return user;
}

// HOC for components that require authentication
export function withAuth<P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> {
  return function AuthenticatedComponent(props: P) {
    const { isAuthenticated, isLoading } = useAuth();
    
    if (isLoading) {
      return <div>Loading...</div>; // Could be replaced with a proper loading component
    }
    
    if (!isAuthenticated) {
      // Redirect to login or show login required message
      window.location.href = '/admin/login';
      return null;
    }
    
    return <Component {...props} />;
  };
}